// MobileMyList — aba MINHA LISTA da superfície MOBILE (touch). Parede A5.
//
// Favoritos do usuário (filme/série/canal) num grid vertical. Reusa a MESMA
// camada de dados da TV (local-db getFavorites, key scopada via
// useScopedQueryKey). Tap num favorito VOD/série → tela de DETALHE; tap num
// canal favorito → player direto (canal não tem detalhe). Empty state quando
// não há favoritos.
//
// Não depende de fonte ativa (favoritos são locais): no_provider não se aplica
// aqui, mas se o usuário nunca favoritou nada mostramos o empty state.

import { useTranslation } from '@app-iptv/i18n'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useScopedQueryKey } from '../hooks/useScopedQueryKey'
import { listState } from '../lib/empty-vs-loading'
import { getFavorites, type Favorite } from '../local-db'

import styles from './MobileCatalog.module.css'
import { MobileGrid } from './MobileGrid'
import type { MobileHomeItem } from './MobileHome'
import { MobileEmpty, MobileLoading } from './MobileStates'
import { MobileVodCard } from './MobileVodCard'

export interface MobileMyListProps {
  /** Tap num favorito → detalhe (VOD/série) ou player (canal). */
  onOpenItem: (item: MobileHomeItem) => void
}

export function MobileMyList({ onOpenItem }: MobileMyListProps): JSX.Element {
  const { t } = useTranslation()

  const favoritesQ = useQuery({
    queryKey: useScopedQueryKey(['favorites', 'all']),
    queryFn: () => getFavorites(),
    staleTime: 60 * 1000,
  })

  const favorites = useMemo(() => favoritesQ.data ?? [], [favoritesQ.data])

  // Mesmo cuidado da TV: no PRIMEIRO frame `favoritesQ.data` é `undefined`, então
  // o `length === 0` sozinho afirmava "sem favoritos" antes de o IndexedDB
  // responder. Query assentada sem dado (erro terminal) cai no empty state, não
  // em spinner preso — `isFetching` termina sempre (retry finito).
  const state = listState({ itemCount: favorites.length, queries: [favoritesQ] })
  if (state === 'loading') {
    return <MobileLoading />
  }
  if (state === 'empty') {
    return (
      <MobileEmpty title={t('empty.noFavoritesTitle')} text={t('empty.noFavoritesDesc')} />
    )
  }

  return (
    <div className={styles.screen} data-screen="mylist">
      <MobileGrid gridId="mylist">
        {favorites.map((f: Favorite) => {
          const kind: 'vod' | 'series' | 'channel' = f.type
          return (
            <MobileVodCard
              key={f.id}
              title={f.title}
              poster={f.poster}
              kind={kind}
              onPress={() => onOpenItem({ id: f.itemId, kind, title: f.title, poster: f.poster })}
            />
          )
        })}
      </MobileGrid>
    </div>
  )
}
